<script setup lang="ts">
  import { computed } from 'vue'

  const props = defineProps({
    highlight: {
      type: Boolean,
      default: false,
    },
    highlightPercentage: {
      type: Number,
      default: 100,
    },
    active: {
      type: Boolean,
      default: false,
    }
  })

  const roundedHighlightPercentage = computed(() => Math.round(props.highlightPercentage / 5)  * 5)

  const lightness = computed(() => {
    const max = 60
    const min = 35

    return (min + (max - min) * (props.highlightPercentage / 100)) + '%'
  })
</script>

<template>
  <button
    class="relative text-xs h-7 gap-2 cursor-pointer appearance-none text-white/75 hover:bg-white/10 hover:text-white transition-all duration-200 px-2.5 py-0.5 rounded -mr-0.5"
    :class="{'animate-shake': highlight && (roundedHighlightPercentage == 5 || roundedHighlightPercentage > 65)}"
    type="button"
  >
    <transition name="fade-slow">
      <span
        v-if="highlight || active"
        class="absolute inset-0 z-10 rounded border border-transparent"
        :class="{
          'highlight animate animate-bounce': highlight,
          'border-white/20 hover:border-white/40 bg-csBlue-600': active,
        }"
      />
    </transition>
    <span class="relative z-20 inset-0 flex items-center gap-2">
      <span v-if="$slots.left">
        <slot name="left" />
      </span>
      <slot />
      <span v-if="$slots.right">
        <slot name="right" />
      </span>
    </span>
  </button>
</template>

<style scoped>
  .animate-shake {
    animation: shake 4s cubic-bezier(.36, .07, .19, .97) both infinite;
  }

  @keyframes shake {
    0% {
      transform: translateX(0);
    }
    5%, 15% {
      transform: translateX(-4px);
    }
    10%, 20% {
      transform: translateX(4px);
    }
    25%, 100% {
      transform: translateX(0);
    }
  }

  .highlight {
    --angle: 0deg;
    --opacity: 1;

    text-align: center;

    --border-size: 1px;
    border: var(--border-size) dotted transparent;
    background-image: linear-gradient(
      to right,
      theme('colors.csBlue.600'),
      theme('colors.csBlue.600')
    ),
    conic-gradient(
      from var(--angle),
      hsl(215, 25%, v-bind(lightness)) 0deg 60deg,
      theme('colors.csBlue.400') 60deg 180deg,
      hsl(215, 25%, v-bind(lightness)) 180deg 240deg,
      theme('colors.csBlue.400') 240deg 360deg
    );
    background-origin: border-box;
    background-clip: padding-box, border-box;
  }

  @property --angle {
    syntax: "<angle>";
    initial-value: 0deg;
    inherits: false;
  }

  @keyframes rotate {
    to {
      --angle: 360deg;
    }
  }

  .highlight {
    animation: rotate 4s linear infinite;
  }

  .fade-slow-enter-active {
    transition: opacity .25s ease;
  }

  .fade-slow-leave-active {
    transition: opacity .1s ease;
  }

  .fade-slow-enter-from,
  .fade-slow-leave-to {
    opacity: 0;
  }
</style>